import React from 'react';
import {
  REVEAL_STAGE_DELAYS_MS,
  buildRevealModel,
} from './plantingRevealUtils';

export function PlantingRevealStep({
  place = null,
  draftSoilPh = '',
  draftSoilDrainageLabel = '',
  draftSoilTypeId = '',
  onStageChange,
}) {
  const [stage, setStage] = React.useState(0);
  const onStageChangeRef = React.useRef(onStageChange);

  React.useEffect(() => {
    onStageChangeRef.current = onStageChange;
  }, [onStageChange]);

  React.useEffect(() => {
    setStage(0);
    if (typeof onStageChangeRef.current === 'function') onStageChangeRef.current(0);
    const timers = REVEAL_STAGE_DELAYS_MS.map((ms, i) =>
      setTimeout(() => {
        const next = i + 1;
        setStage(next);
        if (typeof onStageChangeRef.current === 'function') onStageChangeRef.current(next);
      }, ms)
    );
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [place?.label, place?.zone, place?.ph, place?.drain, place?.first, place?.series, draftSoilPh, draftSoilDrainageLabel, draftSoilTypeId]);

  const model = buildRevealModel({
    place,
    draftSoilPh,
    draftSoilDrainageLabel,
    draftSoilTypeId,
    stage,
  });

  return (
    <section className="plant-reveal" data-testid="plant-reveal-step" data-stage={stage}>
      <div className="plant-reveal__grid">
        <div className="plant-reveal-cross">
          <div className="plant-reveal-cross__head" data-testid="plant-reveal-cross-head">
            CROSS-SECTION · {model.soilSeries}
          </div>
          <div className="plant-reveal-cross__body">
            <div className="plant-reveal-stack" aria-hidden="true">
              {model.soilBands.map((band) => (
                <div
                  key={band.color + String(band.heightPct)}
                  className="plant-reveal-band"
                  style={{
                    height: `${band.heightPct}%`,
                    background: band.color,
                    color: band.fg,
                  }}
                >
                  <span>{band.label}</span>
                </div>
              ))}
            </div>
            <p
              className={`plant-reveal-cross__note${model.crossNoteVisible ? ' is-on' : ''}`}
              data-testid="plant-reveal-cross-note"
            >
              {model.crossNote}
            </p>
          </div>
        </div>

        <div className="plant-reveal-side">
          <div className="plant-reveal-readings">
            <div className="plant-reveal-readings__head">
              <span>THE READINGS</span>
              <span className="plant-reveal-readings__status">{model.status}</span>
            </div>
            <div className="plant-reveal-dials">
              {model.dials.map((dial) => (
                <div
                  key={dial.label}
                  className="plant-reveal-dial"
                  data-testid={`plant-reveal-dial-${dial.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="plant-reveal-gauge">
                    <div className="plant-reveal-gauge__ring" />
                    <div
                      className="plant-reveal-gauge__needle"
                      style={{ transform: `translateX(-50%) rotate(${dial.deg}deg)` }}
                    />
                    <div className="plant-reveal-gauge__hub" />
                    <div className="plant-reveal-gauge__value">{dial.value}</div>
                  </div>
                  <div className="plant-reveal-dial__meta">
                    <div className="plant-reveal-dial__label">{dial.label}</div>
                    <div className="plant-reveal-dial__verdict">{dial.verdict}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="plant-reveal-means">
            <div className="plant-reveal-means__head">WHAT IT MEANS</div>
            <div className="plant-reveal-means__list">
              {model.lines.map((line, idx) => (
                <div
                  key={idx}
                  className={`plant-reveal-line${line.alt ? ' is-alt' : ''}${line.on ? ' is-on' : ''}`}
                  data-testid={`plant-reveal-line-${idx}`}
                >
                  <span
                    className="plant-reveal-line__dot"
                    style={{ background: line.dot }}
                    aria-hidden="true"
                  />
                  <span className="plant-reveal-line__text">{line.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
